/**
 * Consumer Portal Command Palette Provider
 *
 * Provides quick navigation and actions for consumers.
 * Optimized for case status, document uploads, communication, and global search (Issue #236).
 *
 * Enhanced features:
 * - Two-column layout with entity preview
 * - Mode switching between search and chat
 * - Embedded AI chat with entity context
 */

'use client';

import AppsIcon from '@mui/icons-material/Apps';
import ArticleIcon from '@mui/icons-material/Article';
import ChatIcon from '@mui/icons-material/Chat';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DocumentIcon from '@mui/icons-material/Description';
import FolderIcon from '@mui/icons-material/FolderShared';
import HelpIcon from '@mui/icons-material/Help';
import LogoutIcon from '@mui/icons-material/Logout';
import ProposalsIcon from '@mui/icons-material/Receipt';
import QuotesIcon from '@mui/icons-material/RequestQuote';
import SettingsIcon from '@mui/icons-material/Settings';
import RecordsIcon from '@mui/icons-material/TableChart';
import UploadIcon from '@mui/icons-material/Upload';
import {
  CommandPalette,
  type CommandPaletteCategory,
  type CommandPaletteItem,
  type CommandPaletteMode,
  type CommandPaletteRef,
  type EntityPreviewData,
  type GlobalSearchResultItem,
  type PaletteChatChunk,
  type PaletteChatEntityContext,
  useDebouncedValue,
  useI18n,
  usePaletteChat,
} from '@ops-matrix/design-system-ui';
import type { AgentResponseChunk } from '@ops-matrix/shared-types';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { env } from '../../config/env';
import { useConsumerAuth } from '../../contexts/ConsumerAuthContext';
import { getAccessToken } from '../../lib/auth/storage';
import { useGlobalSearchQuery } from '../../lib/trpc/hooks';

// ============================================================================
// Agent Service Chat Integration
// ============================================================================

/** Default agent ID for consumer portal chat */
const CONSUMER_CHAT_AGENT_ID = 'consumer-assistant';

/**
 * Map AgentResponseChunk from API to PaletteChatChunk for UI
 */
function mapAgentChunkToPaletteChunk(chunk: AgentResponseChunk): PaletteChatChunk {
  switch (chunk.type) {
    case 'content':
      return {
        type: 'TEXT',
        content: chunk.content ?? '',
        delta: true,
      };

    case 'sources':
      return {
        type: 'SOURCES',
        sources: chunk.sources?.map((s) => ({
          id: s.id,
          content: s.content,
          score: s.score ?? 0,
        })),
      };

    case 'metadata':
      return {
        type: 'TEXT',
        content: '',
        delta: false,
      };

    case 'done':
      return {
        type: 'FINAL_ANSWER',
        content: chunk.content ?? '',
      };

    case 'error':
      return {
        type: 'ERROR',
        error: {
          code: 'AGENT_ERROR',
          message: chunk.error ?? 'Unknown error from agent',
        },
      };

    default:
      return {
        type: 'TEXT',
        content: '',
        delta: false,
      };
  }
}

/**
 * Create sendFn for AI chat that calls the agent service SSE endpoint
 */
function createAgentChatSendFn(agentId: string): (
  message: string,
  options?: {
    conversationId?: string;
    entityContext?: PaletteChatEntityContext;
    signal?: AbortSignal;
  },
) => AsyncGenerator<PaletteChatChunk, void, unknown> {
  return async function* (
    message: string,
    options?: {
      conversationId?: string;
      entityContext?: PaletteChatEntityContext;
      signal?: AbortSignal;
    },
  ): AsyncGenerator<PaletteChatChunk, void, unknown> {
    const token = getAccessToken();

    if (!token) {
      yield {
        type: 'ERROR',
        error: { code: 'NO_AUTH', message: 'Not authenticated. Please log in again.' },
      };
      return;
    }

    try {
      // Server route is `@Sse(':id/invoke/stream')` (NestJS SSE → GET-only).
      // EventSource can't set custom headers or send a body, so the server
      // contract puts message/conversationId in query params; fetch follows
      // the same shape so a non-streaming follow-up doesn't have to diverge.
      const params = new URLSearchParams({ message });
      if (options?.conversationId) params.set('conversationId', options.conversationId);

      const response = await fetch(`${env.VITE_API_URL}/agents/${agentId}/invoke/stream?${params.toString()}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'text/event-stream',
        },
        signal: options?.signal,
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        yield {
          type: 'ERROR',
          error: {
            code: `HTTP_${response.status}`,
            message: response.status === 401 ? 'Session expired. Please log in again.' : errorText,
          },
        };
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        yield {
          type: 'ERROR',
          error: { code: 'NO_STREAM', message: 'Failed to establish stream connection' },
        };
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';
      let fullContent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        const events = buffer.split('\n\n');
        buffer = events.pop() || '';

        for (const event of events) {
          const lines = event.split('\n');
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const dataStr = line.slice(6).trim();
              if (!dataStr) continue;

              try {
                const chunk = JSON.parse(dataStr) as AgentResponseChunk;
                const paletteChunk = mapAgentChunkToPaletteChunk(chunk);

                if (chunk.type === 'content' && chunk.content) {
                  fullContent += chunk.content;
                }

                yield paletteChunk;

                if (chunk.type === 'done') {
                  yield {
                    type: 'FINAL_ANSWER',
                    content: fullContent,
                  };
                }
              } catch {
                console.warn('[CommandPalette] Failed to parse SSE chunk:', dataStr);
              }
            }
          }
        }
      }

      if (fullContent) {
        yield {
          type: 'FINAL_ANSWER',
          content: fullContent,
        };
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }

      yield {
        type: 'ERROR',
        error: {
          code: 'NETWORK_ERROR',
          message: err instanceof Error ? err.message : 'Failed to connect to AI service',
        },
      };
    }
  };
}

/** Singleton sendFn for the consumer assistant agent */
const agentChatSendFn = createAgentChatSendFn(CONSUMER_CHAT_AGENT_ID);

// ============================================================================
// Context Types
// ============================================================================

interface CommandPaletteContextValue {
  open: () => void;
  close: () => void;
  isOpen: boolean;
}

const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(null);

export const useCommandPaletteContext = (): CommandPaletteContextValue => {
  const context = useContext(CommandPaletteContext);
  if (!context) {
    throw new Error('useCommandPaletteContext must be used within CommandPaletteProvider');
  }
  return context;
};

// ============================================================================
// Provider Component
// ============================================================================

interface CommandPaletteProviderProps {
  children: React.ReactNode;
}

/**
 * Get icon for entity type (Issue #236)
 */
function getEntityIcon(entityType: string) {
  switch (entityType) {
    case 'case':
      return <FolderIcon />;
    case 'document':
      return <DocumentIcon />;
    default:
      return <ArticleIcon />;
  }
}

/**
 * Get status color based on status string
 */
function getStatusColor(status: string): 'success' | 'warning' | 'error' | 'info' | 'default' {
  const statusLower = status.toLowerCase();
  if (['active', 'open', 'approved', 'completed'].includes(statusLower)) return 'success';
  if (['pending', 'in_progress', 'review', 'submitted'].includes(statusLower)) return 'warning';
  if (['closed', 'rejected', 'cancelled'].includes(statusLower)) return 'error';
  if (['new', 'draft'].includes(statusLower)) return 'info';
  return 'default';
}

/**
 * Build preview attributes from search result
 */
/**
 * Build preview attributes for an entity
 * @see Issue #215 - i18n adoption for attribute labels
 */
function buildPreviewAttributes(result: GlobalSearchResultItem, t: (key: string) => string) {
  const attributes: { label: string; value: string; copyable?: boolean }[] = [];

  if (result.description) {
    attributes.push({ label: t('common.description'), value: result.description });
  }

  // Add type-specific attributes
  if (result.entityType === 'case') {
    attributes.push({ label: `${t('entities.case')} ID`, value: result.id, copyable: true });
  } else if (result.entityType === 'document') {
    attributes.push({ label: `${t('entities.document')} ID`, value: result.id, copyable: true });
  }

  return attributes;
}

/**
 * Build quick actions for an entity
 * @see Issue #215 - i18n adoption for quick action labels
 */
function buildQuickActions(
  result: GlobalSearchResultItem,
  navigate: ReturnType<typeof useNavigate>,
  t: (key: string) => string,
) {
  const actions: { id: string; label: string; onClick: () => void }[] = [];

  // View action is always available
  actions.push({
    id: 'view',
    label: t('actions.viewDetails'),
    onClick: () => navigate(result.url),
  });

  // Type-specific actions
  if (result.entityType === 'case') {
    actions.push({
      id: 'documents',
      label: t('actions.viewDocuments'),
      onClick: () => navigate(`${result.url}/documents`),
    });
  }

  return actions;
}

export function CommandPaletteProvider({ children }: CommandPaletteProviderProps): React.ReactElement {
  const navigate = useNavigate();
  const { consumer, logout } = useConsumerAuth();
  const { t } = useI18n();
  const paletteRef = useRef<CommandPaletteRef>(null);
  const [isOpen, setIsOpen] = useState(false);

  // Global search state (Issue #236)
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedQuery = useDebouncedValue(searchQuery, 300);

  // Enhanced palette state (Issue #236 - Phase 6)
  const [mode, setMode] = useState<CommandPaletteMode>('search');
  const [focusedItem, setFocusedItem] = useState<GlobalSearchResultItem | null>(null);
  const [previewData, setPreviewData] = useState<EntityPreviewData | null>(null);
  // Loading state kept for potential future async preview fetching
  const [isPreviewLoading, _setIsPreviewLoading] = useState(false);
  const [chatContext, setChatContext] = useState<EntityPreviewData | null>(null);

  // AI Chat hook with real agent service integration
  const {
    messages: chatMessages,
    isStreaming: isChatStreaming,
    sendMessage: sendChatMessage,
    clearMessages: clearChatHistory,
    setEntityContext: setChatEntityContext,
    clearContext: clearChatContext,
  } = usePaletteChat({
    agentId: CONSUMER_CHAT_AGENT_ID,
    storageKey: 'consumer-portal-palette-chat',
    sendFn: agentChatSendFn,
  });

  // Sync chat context when chatContext state changes
  useEffect(() => {
    // Convert EntityPreviewData to PaletteChatEntityContext
    if (chatContext) {
      const entityContext: PaletteChatEntityContext = {
        entityType: chatContext.entityType,
        entityId: chatContext.id,
        entityLabel: chatContext.title,
        metadata: chatContext.metadata,
      };
      setChatEntityContext(entityContext);
    } else {
      setChatEntityContext(null);
    }
  }, [chatContext, setChatEntityContext]);

  // Global search query - portal filtering handles entity types (case, document)
  const { data: searchResponse, isLoading: isSearching } = useGlobalSearchQuery(
    debouncedQuery.length >= 2
      ? {
          query: debouncedQuery,
          limit: 10,
          includeFacets: true,
        }
      : null,
  );

  // Build preview data from focused item
  useEffect(() => {
    if (!focusedItem) {
      setPreviewData(null);
      return;
    }

    // Build preview data from search result (synchronous - no loading state needed)
    const preview: EntityPreviewData = {
      id: focusedItem.id,
      entityType: focusedItem.entityType,
      title: focusedItem.title,
      status: focusedItem.status
        ? {
            label: focusedItem.status,
            color: getStatusColor(focusedItem.status),
          }
        : undefined,
      attributes: buildPreviewAttributes(focusedItem, t),
      quickActions: buildQuickActions(focusedItem, navigate, t),
      lastUpdated: focusedItem.updatedAt ? new Date(focusedItem.updatedAt) : undefined,
    };

    setPreviewData(preview);
  }, [focusedItem, navigate]);

  // Transform search results to CommandPaletteItem format
  const searchResults = useMemo<CommandPaletteItem[]>(() => {
    if (!searchResponse?.results) return [];
    return searchResponse.results.map((result: GlobalSearchResultItem) => ({
      id: `search-${result.entityType}-${result.id}`,
      label: result.title,
      description: result.description,
      category: `search-${result.entityType}`,
      icon: getEntityIcon(result.entityType),
      keywords: [],
      onSelect: () => {
        navigate(result.url);
        setIsOpen(false);
      },
    }));
  }, [searchResponse, navigate]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setSearchQuery('');
    setMode('search');
    setFocusedItem(null);
    setPreviewData(null);
  }, []);

  // Handle item focus for preview
  const handleItemFocus = useCallback(
    (item: CommandPaletteItem | null) => {
      if (!item || !item.id.startsWith('search-')) {
        setFocusedItem(null);
        return;
      }

      // Extract the entity from search results
      const result = searchResponse?.results?.find(
        (r: GlobalSearchResultItem) => `search-${r.entityType}-${r.id}` === item.id,
      );
      setFocusedItem(result ?? null);
    },
    [searchResponse],
  );

  // Handle view details from preview
  const handleViewDetails = useCallback(
    (entity: EntityPreviewData) => {
      const result = searchResponse?.results?.find((r: GlobalSearchResultItem) => r.id === entity.id);
      if (result) {
        navigate(result.url);
        handleClose();
      }
    },
    [searchResponse, navigate, handleClose],
  );

  // Handle preview quick action
  const handlePreviewAction = useCallback(
    (actionId: string, entity: EntityPreviewData) => {
      const result = searchResponse?.results?.find((r: GlobalSearchResultItem) => r.id === entity.id);
      if (!result) return;

      if (actionId === 'view') {
        navigate(result.url);
        handleClose();
      } else if (actionId === 'documents') {
        navigate(`${result.url}/documents`);
        handleClose();
      }
    },
    [searchResponse, navigate, handleClose],
  );

  // Handle starting chat about an entity
  const handleStartChat = useCallback((entity: EntityPreviewData) => {
    setChatContext(entity);
    setMode('chat');
  }, []);

  // Handle chat send
  const handleChatSend = useCallback(
    (message: string) => {
      sendChatMessage(message);
    },
    [sendChatMessage],
  );

  // Handle clearing chat context
  const handleClearChatContext = useCallback(() => {
    setChatContext(null);
    clearChatContext();
  }, [clearChatContext]);

  // Handle clearing chat history
  const handleClearChatHistory = useCallback(() => {
    clearChatHistory();
  }, [clearChatHistory]);

  // Keyboard shortcut (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        handleOpen();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleOpen]);

  // Categories for consumer portal (with search result categories - Issue #236)
  const categories: CommandPaletteCategory[] = useMemo(
    () => [
      { id: 'navigation', label: t('category.goTo'), icon: <DashboardIcon fontSize="small" /> },
      { id: 'actions', label: t('category.quickActions'), icon: <UploadIcon fontSize="small" /> },
      { id: 'help', label: t('category.helpSupport'), icon: <HelpIcon fontSize="small" /> },
      // Search result categories (Issue #236)
      { id: 'search-case', label: t('search.results.cases'), icon: <FolderIcon fontSize="small" />, priority: 10 },
      {
        id: 'search-document',
        label: t('search.results.documents'),
        icon: <DocumentIcon fontSize="small" />,
        priority: 11,
      },
    ],
    [t],
  );

  // Items for consumer portal
  const items: CommandPaletteItem[] = useMemo(
    () => [
      // Navigation
      {
        id: 'dashboard',
        label: t('command.dashboard'),
        description: t('command.caseStatus'),
        icon: <DashboardIcon />,
        category: 'navigation',
        shortcut: ['G', 'D'],
        onSelect: () => {
          navigate('/');
          handleClose();
        },
      },
      {
        id: 'documents',
        label: t('command.documents'),
        description: t('command.uploadFiles'),
        icon: <DocumentIcon />,
        category: 'navigation',
        shortcut: ['G', 'O'],
        onSelect: () => {
          navigate('/documents');
          handleClose();
        },
      },
      {
        id: 'profile',
        label: t('command.profile'),
        description: t('command.updateContact'),
        icon: <SettingsIcon />,
        category: 'navigation',
        shortcut: ['G', 'P'],
        onSelect: () => {
          navigate('/profile');
          handleClose();
        },
      },
      {
        id: 'applications',
        label: t('command.applications'),
        description: t('command.viewApplications'),
        icon: <AppsIcon />,
        category: 'navigation',
        shortcut: ['G', 'A'],
        onSelect: () => {
          navigate('/applications');
          handleClose();
        },
      },
      {
        id: 'proposals',
        label: t('command.proposals'),
        description: t('command.viewProposals'),
        icon: <ProposalsIcon />,
        category: 'navigation',
        onSelect: () => {
          navigate('/proposals');
          handleClose();
        },
      },
      {
        id: 'quotes',
        label: t('command.quotes'),
        description: t('command.viewQuotes'),
        icon: <QuotesIcon />,
        category: 'navigation',
        onSelect: () => {
          navigate('/quotes');
          handleClose();
        },
      },
      {
        id: 'records',
        label: t('command.records'),
        description: t('command.viewRecords'),
        icon: <RecordsIcon />,
        category: 'navigation',
        onSelect: () => {
          navigate('/records');
          handleClose();
        },
      },

      // Actions
      {
        id: 'upload-document',
        label: t('command.uploadDoc'),
        description: t('command.newDocument'),
        icon: <UploadIcon />,
        category: 'actions',
        shortcut: ['U'],
        onSelect: () => {
          navigate('/documents?action=upload');
          handleClose();
        },
      },

      // Help
      {
        id: 'help-center',
        label: t('command.helpCenter'),
        description: t('command.commonQuestions'),
        icon: <ArticleIcon />,
        category: 'help',
        onSelect: () => {
          navigate('/help');
          handleClose();
        },
      },
      {
        id: 'contact-support',
        label: t('command.contactSupport'),
        description: t('command.supportHelp'),
        icon: <ChatIcon />,
        category: 'help',
        onSelect: () => {
          navigate('/support');
          handleClose();
        },
      },
      ...(consumer
        ? [
            {
              id: 'logout',
              label: t('auth.signOut'),
              description: t('auth.loggedInAs', { email: consumer.email }),
              icon: <LogoutIcon />,
              category: 'help',
              onSelect: () => {
                logout();
                navigate('/login');
                handleClose();
              },
            },
          ]
        : []),
    ],
    [navigate, consumer, logout, handleClose, t],
  );

  // Combine navigation items with search results (Issue #236)
  const allItems = useMemo(() => {
    // When there's a search query, show search results and navigation items
    if (searchQuery.length >= 2) {
      return [...searchResults, ...items];
    }
    // Otherwise, just show navigation items
    return items;
  }, [items, searchResults, searchQuery]);

  const contextValue = useMemo<CommandPaletteContextValue>(
    () => ({
      open: handleOpen,
      close: handleClose,
      isOpen,
    }),
    [handleOpen, handleClose, isOpen],
  );

  // Handle search query change (Issue #236)
  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  return (
    <CommandPaletteContext.Provider value={contextValue}>
      {children}
      <CommandPalette
        ref={paletteRef}
        open={isOpen}
        categories={categories}
        items={allItems}
        onClose={handleClose}
        placeholder={t('search.placeholder')}
        onSearchChange={handleSearchChange}
        isSearching={isSearching}
        // Enhanced features (Issue #236 - Phase 6)
        enablePreview={searchQuery.length >= 2}
        previewWidth={320}
        previewData={previewData}
        isPreviewLoading={isPreviewLoading}
        onItemFocus={handleItemFocus}
        onViewDetails={handleViewDetails}
        onPreviewAction={handlePreviewAction}
        enableChat
        mode={mode}
        onModeChange={setMode}
        chatMessages={chatMessages}
        isChatStreaming={isChatStreaming}
        chatContext={chatContext}
        onChatSend={handleChatSend}
        onChatClearContext={handleClearChatContext}
        onChatClearHistory={handleClearChatHistory}
        onStartChat={handleStartChat}
      />
    </CommandPaletteContext.Provider>
  );
}

export default CommandPaletteProvider;
